import React, { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  structuredData?: object;
}

const SEOHeadNative: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords = "électricien, électricité, bâtiment, Viesly, Aisne, Nord, installation électrique, rénovation, habilitation électrique, domotique, maintenance industrielle",
  canonical,
  ogType = "website",
  ogImage = "/images/ozarkselec-logo-bleu.png",
  structuredData
}) => {
  const siteName = "OZARKS ELEC";
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const siteUrl = "https://ozarkselec.com";
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

  useEffect(() => {
    // Titre de la page
    document.title = fullTitle;

    // Fonction pour créer ou mettre à jour un meta tag
    const setMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Fonction pour créer ou mettre à jour un link tag
    const setLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Meta tags de base
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setMetaTag('author', 'OZARKS ELEC');
    setMetaTag('robots', 'index, follow');
    setMetaTag('viewport', 'width=device-width, initial-scale=1.0');

    // Canonical URL
    setLinkTag('canonical', fullCanonical);

    // Open Graph / Facebook
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', fullCanonical, true);
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', `${siteUrl}${ogImage}`, true);
    setMetaTag('og:site_name', siteName, true);
    setMetaTag('og:locale', 'fr_FR', true);

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:url', fullCanonical, true);
    setMetaTag('twitter:title', fullTitle, true);
    setMetaTag('twitter:description', description, true);
    setMetaTag('twitter:image', `${siteUrl}${ogImage}`, true);

    // Géolocalisation
    setMetaTag('geo.region', 'FR-02');
    setMetaTag('geo.placename', 'Viesly, France');
    setMetaTag('geo.position', '50.068;3.427');
    setMetaTag('ICBM', '50.068, 3.427');

    // Informations de contact
    setMetaTag('contact', 'ozarkselec@gmail.com');
    setMetaTag('phone', '+33630984578');

    // Données structurées JSON-LD
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      
      if (!script) {
        script = document.createElement('script') as HTMLScriptElement;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

  }, [title, description, keywords, canonical, ogType, ogImage, structuredData, fullTitle, fullCanonical, siteUrl, siteName]);

  return null; // Ce composant ne rend rien visuellement
};

export default SEOHeadNative;